/**
 * Product variant sync.
 * Handles: sort-dropdown variant selection, price update,
 * featured image swap, sold out state, URL history.
 *
 * Reads product JSON from <script data-product-json>.
 * Uses window.theme.moneyFormat for price formatting.
 */
(function () {
  'use strict';

  var productJSON = document.querySelector('[data-product-json]');
  if (!productJSON) return;

  var product = JSON.parse(productJSON.textContent);
  var variants = product.variants;
  var options = product.options;

  /* DOM refs */
  var priceContainer = document.querySelector('[data-product-price]');
  var currentPriceEl = document.querySelector('[data-current-price]');
  var comparePriceEl = document.querySelector('[data-compare-price]');
  var variantIdInput = document.querySelector('[data-variant-id]');
  var submitBtn = document.querySelector('.product-form__submit');
  var selectorContainer = document.querySelector('[data-variant-selectors]');

  /* State: current option selections */
  var selectedOptions = [];
  for (var i = 0; i < options.length; i++) {
    selectedOptions[i] = getInitialOption(i);
  }

  function getInitialOption(index) {
    if (selectorContainer) {
      /* Sort-dropdown style: read from the label text "Option: Value" */
      var dropdown = selectorContainer.querySelector('[data-option-index="' + index + '"]');
      if (dropdown) {
        var selected = dropdown.querySelector('[aria-selected="true"]');
        if (selected) return selected.getAttribute('data-option-value');
        var label = dropdown.querySelector('[data-variant-label]');
        if (label) {
          var text = label.textContent;
          var colonIdx = text.indexOf(':');
          if (colonIdx !== -1) return text.substring(colonIdx + 1).trim();
        }
      }
    }
    var first = variants[0];
    return first ? first.options[index] : '';
  }

  /* ── Find variant matching current selections ── */
  function findVariant() {
    for (var i = 0; i < variants.length; i++) {
      var match = true;
      for (var j = 0; j < selectedOptions.length; j++) {
        if (variants[i].options[j] !== selectedOptions[j]) {
          match = false;
          break;
        }
      }
      if (match) return variants[i];
    }
    return null;
  }

  /* ── Update UI for selected variant ── */
  function updateVariant(variant) {
    if (!variant) {
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Unavailable';
      }
      return;
    }

    /* Price */
    if (currentPriceEl) {
      currentPriceEl.textContent = formatMoney(variant.price);
    }
    if (comparePriceEl && priceContainer) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        priceContainer.classList.add('product-hero__price--sale');
      } else {
        comparePriceEl.textContent = '';
        priceContainer.classList.remove('product-hero__price--sale');
      }
    }

    /* Hidden input */
    if (variantIdInput) {
      variantIdInput.value = variant.id;
    }

    /* Submit button */
    if (submitBtn) {
      if (variant.available) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Add To Cart';
      } else {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sold Out';
      }
    }

    /* Featured image swap */
    if (variant.featured_image) {
      var galleryImg = document.querySelector('[data-gallery-image]');
      if (galleryImg) {
        var newSrc = getSizedUrl(variant.featured_image.src, 900);
        galleryImg.src = newSrc;
        galleryImg.srcset =
          getSizedUrl(variant.featured_image.src, 600) + ' 600w, ' +
          getSizedUrl(variant.featured_image.src, 900) + ' 900w, ' +
          getSizedUrl(variant.featured_image.src, 1200) + ' 1200w';
        galleryImg.alt = variant.featured_image.alt || product.title;
        galleryImg.setAttribute('data-full-src', getSizedUrl(variant.featured_image.src, 1800));
      }
    }

    /* URL history */
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(null, '', url.toString());
    }

    /* Announce the change so the gallery can filter the photo strip to the selected
       colour. An event rather than a direct call: the gallery already owns the strip,
       and reading the selector markup from there would couple two modules to the
       same DOM. */
    document.dispatchEvent(
      new CustomEvent('product:variantchange', {
        detail: { variant: variant, options: selectedOptions.slice() }
      })
    );

    debug.trace('productSync:variantChange', {
      variantId: variant.id,
      available: variant.available,
      price: variant.price
    });
  }

  /* ── Price formatting ── */
  function formatMoney(cents) {
    var format = window.theme.moneyFormat;
    var value = (cents / 100).toFixed(2);
    return format
      .replace('{{amount}}', value)
      .replace('{{ amount }}', value)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{ amount_no_decimals }}', Math.round(cents / 100).toString())
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','))
      .replace('{{ amount_with_comma_separator }}', value.replace('.', ','));
  }

  /* ── Image URL helpers ── */
  function getSizedUrl(src, width) {
    if (!src) return '';
    return src.replace(/\.(jpg|jpeg|png|gif|webp)/, '_' + width + 'x.$1');
  }

  /* ── Update dropdown label text ── */
  function updateDropdownLabel(optionIndex, value) {
    if (!selectorContainer) return;
    var dropdown = selectorContainer.querySelector('[data-option-index="' + optionIndex + '"]');
    if (!dropdown) return;

    /* Update label: "OptionName: Value" */
    var label = dropdown.querySelector('[data-variant-label]');
    if (label) {
      var optionName = options[optionIndex];
      label.textContent = optionName + ': ' + value;
    }

    /* Update aria-selected state */
    dropdown.querySelectorAll('[role="option"]').forEach(function (item) {
      if (item.getAttribute('data-option-value') === value) {
        item.setAttribute('aria-selected', 'true');
      } else {
        item.removeAttribute('aria-selected');
      }
    });
  }

  /* ── Event listeners ── */
  function bindEvents() {
    if (!selectorContainer) return;

    /* Sort-dropdown variant options */
    var dropdowns = selectorContainer.querySelectorAll('[data-variant-dropdown]');
    dropdowns.forEach(function (dropdown) {
      var idx = parseInt(dropdown.getAttribute('data-option-index'), 10);
      dropdown.querySelectorAll('[role="option"]').forEach(function (item) {
        item.addEventListener('click', function () {
          var value = this.getAttribute('data-option-value');
          selectedOptions[idx] = value;
          updateDropdownLabel(idx, value);
          updateVariant(findVariant());
        });
      });
    });
  }

  /* ── Init ── */
  bindEvents();
  debug.trace('productSync:init', { variantCount: variants.length });
})();
